// facility-ecommerce/src/components/ConfirmationModal.js
//
// R-UI-LEFTOVERS A2 (2026-11-03): the `confirm` variant (420px). The
// confirming button is the variant's drawn primary (`primaryAction`) —
// the solid red (`primaryTone="danger"`) when `isDangerous` — with Cancel
// drawn to its left, both keeping their `${testId}-confirm` / `-cancel`
// hooks; the input's label is the form kit's (ui/Field). The props
// contract is unchanged for every caller.
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from './ui/Modal';
import Field from './ui/Field';

const ConfirmationModal = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isDangerous = false,
  customContent,
  // Input variant — when inputLabel is provided, renders an input/textarea
  // and onConfirm receives the input value as its argument.
  inputLabel,
  inputPlaceholder,
  inputDefaultValue = '',
  inputRequired = false,
  inputMultiline = false,
  testId = 'confirmation-modal',
  // When this confirm is opened from inside a drawer (ShipmentDrawer) or
  // from a modal that is itself over the drawer (RefundModal), it must
  // opt into the --over-drawer z-index modifiers or it renders BEHIND the
  // parent: visible but washed out under the scrim, and unclickable.
  // (This was `modalZIndex`, which set an inline z-index and could never
  // beat the !important base rules — inert wherever it was passed.)
  overDrawer = false,
  // Opened from a modal that is itself over the drawer (RefundModal).
  overModal = false,
  // Opened from the image picker (ModalStack's layer, above every ff-modal
  // tier) — Cowork run 28, A5.
  overPicker = false,
  // A confirm with nothing to confirm (e.g. every row of a preview was
  // refused) shows its Confirm disabled rather than absent.
  disableConfirm = false,
}) => {
  const [inputValue, setInputValue] = useState(inputDefaultValue);
  const inputRef = useRef(null);
  // Local guard — block double-click on Confirm from firing onConfirm twice
  // before the parent's submitting state propagates. Critical for money-moving
  // actions (refunds) where duplicate fires = duplicate Stripe charges.
  const [submitting, setSubmitting] = useState(false);

  // Reset submitting whenever the modal closes/opens, so a re-show after a
  // prior submission doesn't stay disabled.
  useEffect(() => {
    if (!isOpen) setSubmitting(false);
  }, [isOpen]);

  // Reset input value whenever the modal opens with a (possibly new) default
  useEffect(() => {
    if (isOpen) {
      setInputValue(inputDefaultValue);
    }
  }, [isOpen, inputDefaultValue]);

  // Auto-focus the input when the modal opens with an input
  useEffect(() => {
    if (isOpen && inputLabel && inputRef.current) {
      const t = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(t);
    }
  }, [isOpen, inputLabel]);

  const hasInput = Boolean(inputLabel);
  const trimmedValue = inputValue.trim();
  const confirmDisabled = hasInput && inputRequired && trimmedValue.length === 0;

  const handleConfirm = async () => {
    if (confirmDisabled || submitting) return;
    setSubmitting(true);
    try {
      // Await it: most callers are async, and `submitting` used to be
      // cleared ONLY by the close effect above. Callers close the modal on
      // success but leave it open on failure to show the error — so a
      // rejected action left Confirm disabled forever and the dialog
      // looking hung, with no way forward but Cancel. Seen on beta
      // 2026-08-21 marking an order shipped without a tracking number:
      // the API returned a clean 400 and the operator saw a dead button.
      await (hasInput ? onConfirm(inputValue) : onConfirm());
    } catch (err) {
      // handleConfirm is async now, so a throwing onConfirm would become an
      // unhandled rejection instead of a visible event-handler error. Every
      // current caller handles its own errors, so this is belt-and-braces —
      // console.warn deliberately, since the production build strips
      // console.log/.debug/.info but keeps warn.
      console.warn('[ConfirmationModal] onConfirm threw:', err);
    } finally {
      // On success the parent closes the modal and the effect resets this
      // too — harmless. On failure this is the only thing that re-arms the
      // button so the operator can fix the input and retry.
      //
      // INVARIANT this relies on: every caller closes the modal
      // SYNCHRONOUSLY on success, before awaiting anything. That close is
      // committed in React's batch ahead of this microtask, so the Confirm
      // button is already unmounted by the time we re-arm and there is no
      // frame in which it is both visible and enabled — which is what keeps
      // the double-click guard honest for refunds, where a duplicate fire
      // means a duplicate Stripe charge. A caller that closes the modal
      // only AFTER an await would reopen that window.
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    // Enter submits for single-line inputs (textarea allows newlines)
    if (e.key === 'Enter' && hasInput && !inputMultiline && !confirmDisabled) {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      variant="confirm"
      // Design System v1 §2.2: the SOLID red exists only on a dialog's
      // confirm step — this one. Everywhere else danger is an outline.
      primaryTone={isDangerous ? 'danger' : 'primary'}
      primaryAction={{
        label: confirmText,
        onClick: handleConfirm,
        disabled: confirmDisabled || submitting || disableConfirm,
        'data-testid': `${testId}-confirm`,
      }}
      cancelLabel={cancelText}
      onCancel={onCancel}
      cancelAttrs={{ 'data-testid': `${testId}-cancel` }}
      overDrawer={overDrawer}
      overModal={overModal}
      overPicker={overPicker}
      // R5 B2.5 (rule 10): a confirm STACKED over another dialog claims
      // that dialog's suspension while it is open, so Escape answers only
      // the confirm and the Tab trap is the confirm's.
      suspendBeneath={overModal || overPicker}
    >
      <div data-testid={testId}>
        {message && <p style={{ whiteSpace: 'pre-line', marginBottom: hasInput ? 'var(--ff-space-md)' : 0 }}>{message}</p>}
        {customContent}
        {hasInput && (
          <Field
            label={inputLabel}
            required={inputRequired}
            style={{ marginTop: 'var(--ff-space-sm)' }}
          >
            {inputMultiline ? (
              <textarea
                ref={inputRef}
                id={`${testId}-input`}
                className="form-control"
                rows={3}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={inputPlaceholder}
                data-testid={`${testId}-input`}
              />
            ) : (
              <input
                ref={inputRef}
                id={`${testId}-input`}
                type="text"
                className="form-control"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={inputPlaceholder}
                data-testid={`${testId}-input`}
              />
            )}
          </Field>
        )}
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
